import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as analytics from '../lib/analytics';
import { apiClient } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import type { TaskRegistrationInput } from '../lib/types';
import { parseUTCDate } from '../lib/utils';
import { ACTIVE_TASKS_FETCH_LIMIT } from '../lib/constants';
import { getTaskTerminalNotification } from '../lib/task-notifications';
import {
  TaskLifecycleStore,
  isTerminalTaskStatus,
  getTaskExecutionKey,
} from '../lib/task-lifecycle';
import type {
  TaskState,
  TaskManagerState,
  TaskType,
  TaskEntityType,
  TaskIngestionOptions,
  TaskIngestionResult,
} from '../lib/task-lifecycle';

export type { TaskState, TaskType, TaskEntityType } from '../lib/task-lifecycle';
export { getTaskExecutionKey } from '../lib/task-lifecycle';

interface TaskManagerActions {
  registerTask: (
    task: TaskRegistrationInput,
    displayName?: string,
    options?: TaskIngestionOptions
  ) => void;
  updateTask: (taskId: string, update: Partial<TaskState>, options?: TaskIngestionOptions) => void;
  removeTask: (taskId: string) => void;
  markSeen: (taskIds: string[]) => void;
  markAllSeen: () => void;
  cancelTask: (taskId: string) => Promise<void>;
  dismissTask: (taskId: string) => void;
  clearRecentCompleted: () => void;
  refreshActiveTasks: () => Promise<void>;
}

type TaskManagerContextValue = TaskManagerState & TaskManagerActions;
const TaskManagerContext = createContext<TaskManagerContextValue | undefined>(undefined);

export const TaskManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = useRef(new TaskLifecycleStore());
  const [state, setState] = useState(storeRef.current.state);
  const hydrationRef = useRef(new Map<string, Promise<void>>());
  const queryClient = useQueryClient();

  const publish = useCallback((transition: TaskIngestionResult) => {
    if (transition.changed) setState(transition.state);
    const task = transition.terminalEvent;
    if (!task) return;
    analytics.taskCompleted({
      type: task.taskType,
      status: task.status as 'completed' | 'failed' | 'cancelled',
      entity_type: task.entityType,
      entity_id: task.entityId,
      ...(task.startedAt && task.completedAt
        ? { duration_ms: task.completedAt.getTime() - task.startedAt.getTime() }
        : {}),
    });
    const notification = getTaskTerminalNotification(task);
    if (notification) {
      toast[notification.kind](notification.title, {
        id: notification.id,
        ...(notification.description ? { description: notification.description } : {}),
      });
    }
  }, []);

  const apiResponseToTaskState = useCallback(
    (response: TaskRegistrationInput, displayName?: string): TaskState => ({
      taskId: response.task_id,
      taskType: response.task_type as TaskType,
      status: response.status,
      progress: response.progress,
      progressMessage: response.progress_message,
      entityType: response.entity_type as TaskEntityType,
      entityId: response.entity_id,
      createdAt: parseUTCDate(response.created_at) ?? new Date(0),
      ...(displayName !== undefined ? { displayName } : {}),
      ...(response.started_at !== undefined
        ? { startedAt: parseUTCDate(response.started_at) ?? undefined }
        : {}),
      ...(response.completed_at !== undefined
        ? { completedAt: parseUTCDate(response.completed_at) ?? undefined }
        : {}),
      ...(response.payload !== undefined ? { payload: response.payload } : {}),
      ...(response.result !== undefined ? { result: response.result } : {}),
      ...(response.error !== undefined ? { error: response.error } : {}),
      ...(response.attempts !== undefined ? { attempts: response.attempts } : {}),
    }),
    []
  );

  const registerTask = useCallback(
    (response: TaskRegistrationInput, displayName?: string, options: TaskIngestionOptions = {}) => {
      const task = apiResponseToTaskState(response, displayName);
      const requiresHydration =
        isTerminalTaskStatus(task.status) &&
        (response.attempts === undefined ||
          response.completed_at === undefined ||
          (task.status === 'completed' && response.result === undefined) ||
          (task.status === 'failed' && response.error === undefined));
      publish(storeRef.current.ingest(task, { ...options, notify: !requiresHydration }));
      if (!requiresHydration) return;
      const executionKey = getTaskExecutionKey(
        storeRef.current.state.recentCompleted.find((item) => item.taskId === task.taskId) ?? task
      );
      if (hydrationRef.current.has(executionKey)) return;
      const hydration = (async () => {
        try {
          const requestStartedAt = Date.now();
          const authoritative = await apiClient.getTask(task.taskId);
          publish(
            storeRef.current.ingest(apiResponseToTaskState(authoritative, displayName), {
              expectedExecutionKey: executionKey,
              requestStartedAt,
            })
          );
        } catch {
          // Incomplete check results remain neutral; they never imply approval.
          publish(storeRef.current.ingest(task, { expectedExecutionKey: executionKey }));
        } finally {
          hydrationRef.current.delete(executionKey);
        }
      })();
      hydrationRef.current.set(executionKey, hydration);
    },
    [apiResponseToTaskState, publish]
  );

  const updateTask = useCallback(
    (taskId: string, update: Partial<TaskState>, options: TaskIngestionOptions = {}) => {
      const task = storeRef.current.state.activeTasks.get(taskId);
      if (!task) return;
      publish(storeRef.current.ingest({ ...task, ...update }, options));
    },
    [publish]
  );

  const removeTask = useCallback((taskId: string) => {
    setState(storeRef.current.removeTask(taskId));
  }, []);

  const markSeen = useCallback((taskIds: string[]) => {
    setState(storeRef.current.markSeen(taskIds));
    apiClient.markTasksSeen(taskIds).catch(() => {});
  }, []);

  const markAllSeen = useCallback(() => {
    const taskIds = storeRef.current.state.recentCompleted.map((task) => task.taskId);
    setState(storeRef.current.markAllSeen());
    if (taskIds.length > 0) apiClient.markTasksSeen(taskIds).catch(() => {});
  }, []);

  const cancelTask = useCallback(
    async (taskId: string) => {
      const task = storeRef.current.state.activeTasks.get(taskId);
      if (!task || task.status !== 'pending') {
        toast.error('Cannot cancel task', { description: 'Only pending tasks can be cancelled' });
        return;
      }
      try {
        await apiClient.cancelTask(taskId);
        publish(
          storeRef.current.ingest(
            { ...task, status: 'cancelled', progressMessage: 'Cancelled by user' },
            { expectedExecutionKey: getTaskExecutionKey(task), confirmedCancellation: true }
          )
        );
        void queryClient.refetchQueries({ queryKey: queryKeys.credits });
      } catch {
        toast.error('Failed to cancel task');
      }
    },
    [publish, queryClient]
  );

  const dismissTask = useCallback((taskId: string) => {
    setState(storeRef.current.dismissTask(taskId));
  }, []);

  const clearRecentCompleted = useCallback(() => {
    setState(storeRef.current.clearRecentCompleted());
  }, []);

  const refreshActiveTasks = useCallback(async () => {
    const requestStartedAt = Date.now();
    try {
      const [pendingResponse, processingResponse] = await Promise.all([
        apiClient.listTasks({ status: 'pending', limit: ACTIVE_TASKS_FETCH_LIMIT }),
        apiClient.listTasks({ status: 'processing', limit: ACTIVE_TASKS_FETCH_LIMIT }),
      ]);
      for (const task of [...pendingResponse.data, ...processingResponse.data]) {
        registerTask(task, task.entity_name, { requestStartedAt });
      }
    } catch (error) {
      console.warn('Failed to refresh active tasks:', error);
    }
  }, [registerTask]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (state.activeTasks.size > 0) {
        event.preventDefault();
        event.returnValue = 'You have tasks in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.activeTasks.size]);

  const value = useMemo(
    (): TaskManagerContextValue => ({
      ...state,
      registerTask,
      updateTask,
      removeTask,
      markSeen,
      markAllSeen,
      cancelTask,
      dismissTask,
      clearRecentCompleted,
      refreshActiveTasks,
    }),
    [
      state,
      registerTask,
      updateTask,
      removeTask,
      markSeen,
      markAllSeen,
      cancelTask,
      dismissTask,
      clearRecentCompleted,
      refreshActiveTasks,
    ]
  );

  return <TaskManagerContext.Provider value={value}>{children}</TaskManagerContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export function useTaskManager(): TaskManagerContextValue {
  const context = useContext(TaskManagerContext);
  if (!context) {
    throw new Error('useTaskManager must be used within a TaskManagerProvider');
  }
  return context;
}

/**
 * Get an active task by its ID
 */
export function useActiveTask(taskId: string): TaskState | undefined {
  const { activeTasks } = useTaskManager();
  return activeTasks.get(taskId);
}

/**
 * Get all active tasks for a specific entity (design or batch)
 */
export function useActiveTasksForEntity(entityType: TaskEntityType, entityId: string): TaskState[] {
  const { activeTasks } = useTaskManager();
  return useMemo(
    () =>
      Array.from(activeTasks.values()).filter(
        (task) => task.entityType === entityType && task.entityId === entityId
      ),
    [activeTasks, entityType, entityId]
  );
}

/**
 * Get count of active processing tasks
 */
export function useProcessingTaskCount(): number {
  const { activeTasks } = useTaskManager();
  return useMemo(
    () =>
      Array.from(activeTasks.values()).filter(
        (task) => task.status === 'processing' || task.status === 'pending'
      ).length,
    [activeTasks]
  );
}
